/** Orchestrates DataView loading, global cohort ordering, search and item navigation. */
import {ArrowBack, FilterList, Search} from "@mui/icons-material";
import {
   Alert,
   Box,
   Button,
   CircularProgress,
   IconButton,
   Link,
   Menu,
   MenuItem,
   Stack,
   TextField,
   Tooltip,
   Typography,
   useMediaQuery,
   useTheme
} from "@mui/material";
import type {DrilldownResult} from "@michaeltoft/c2s-statistics-dashboard";
import {KeyboardEvent, useEffect, useMemo, useState} from "react";
import {DataViewTypeEnum} from "../../../Queries";
import {DataViewColumnType} from "../../../components/dataview/DataViewColumns";
import {useDataView} from "../../../components/dataview/useDataView";
import {
   buildCohortRows,
   CohortSort,
   defaultCohortSort,
   localizedValue,
   nextCohortId,
   sortCohortRows
} from "./BrandItemCohort";
import {BrandItemDrilldownDetail} from "./BrandItemDrilldownDetail";
import {BrandItemDrilldownList} from "./BrandItemDrilldownList";
import {drilldownTypeLabel} from "./DashboardDrilldownRouting";
import {useBrandItemCohortItems} from "./useBrandItemCohortItems";

interface BrandItemDrilldownWorkspaceProps {
   brandHouseId: string;
   viewId: string;
   relatedViews: DrilldownResult[];
   onClose: () => void;
}

/** Identifies controls where cohort arrow-key navigation must not intercept input. */
const isEditableTarget = (target: EventTarget | null): boolean => {
   const element = target as HTMLElement | null;
   return !!element && (
      ["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName)
      || element.isContentEditable
   );
};

/** Describes one metadata sort in plain brand-facing language. */
const sortOptionLabel = (
   label: string,
   dataType: DataViewColumnType,
   direction: "ASC" | "DESC"
): string => {
   if (dataType === DataViewColumnType.TEXT) {
      return `${label}: ${direction === "ASC" ? "A to Z" : "Z to A"}`;
   }
   return `${label}: ${direction === "ASC" ? "low to high" : "high to low"}`;
};

/** Presents a complete metadata-sorted cohort beside one actionable product detail. */
export const BrandItemDrilldownWorkspace = ({
   brandHouseId,
   viewId,
   relatedViews,
   onClose
}: BrandItemDrilldownWorkspaceProps) => {
   const theme = useTheme();
   const narrow = useMediaQuery(theme.breakpoints.down("md"));
   const dataView = useDataView(viewId);
   const columns = dataView.annotations?.columns ?? [];
   const sourceRows = useMemo(
      () => buildCohortRows(dataView.rowIds, dataView.annotations),
      [dataView.annotations, dataView.rowIds]
   );
   const [chosenSort, setChosenSort] = useState<CohortSort>();
   const activeSort = chosenSort ?? defaultCohortSort(dataView.annotations);
   const sortedRows = useMemo(
      () => sortCohortRows(sourceRows, columns, activeSort),
      [activeSort, columns, sourceRows]
   );
   const orderedIds = useMemo(() => sortedRows.map(row => row.id), [sortedRows]);
   const itemState = useBrandItemCohortItems(orderedIds);
   const [selectedId, setSelectedId] = useState<string>();
   const [search, setSearch] = useState("");
   const [searchOpen, setSearchOpen] = useState(false);
   const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);
   const [showMobileDetail, setShowMobileDetail] = useState(false);
   const [actionBusy, setActionBusy] = useState(false);
   const locale = typeof navigator === "undefined" ? undefined : navigator.language;
   const sortOptions = useMemo(() => columns.flatMap(column => (["DESC", "ASC"] as const).map(direction => ({
      value: `${column.key}:${direction}`,
      sort: {key: column.key, direction},
      label: sortOptionLabel(column.label, column.dataType, direction)
   }))), [columns]);
   const activeSortValue = activeSort ? `${activeSort.key}:${activeSort.direction}` : "";
   const activeSortLabel = sortOptions.find(option => option.value === activeSortValue)?.label ?? "DataView order";

   useEffect(() => {
      setChosenSort(undefined);
      setSearch("");
      setSearchOpen(false);
      setSortAnchor(null);
      setShowMobileDetail(false);
      setActionBusy(false);
   }, [viewId]);

   useEffect(() => {
      setSelectedId(current => current && sourceRows.some(row => row.id === current)
         ? current
         : sortedRows[0]?.id);
   }, [sourceRows, sortedRows]);

   const visibleRows = useMemo(() => {
      const term = search.trim().toLocaleLowerCase();
      if (!term) {
         return sortedRows;
      }
      return sortedRows.filter(row => {
         const item = itemState.itemsById.get(row.id);
         if (!item) {
            return false;
         }
         const searchable = [
            localizedValue(item.resolved?.nameLocalized, locale, ""),
            item.resolved?.itemSku,
            item.itemNr,
            row.id
         ].filter(value => value !== null && value !== undefined).join(" ").toLocaleLowerCase();
         return searchable.includes(term);
      });
   }, [itemState.itemsById, locale, search, sortedRows]);

   const selectedRow = sortedRows.find(row => row.id === selectedId) ?? sortedRows[0];
   const selectedPosition = selectedRow ? sortedRows.findIndex(row => row.id === selectedRow.id) : -1;

   /** Moves selection through the globally sorted cohort without wrapping. */
   const moveSelection = (delta: -1 | 1): void => {
      const nextId = nextCohortId(orderedIds, selectedRow?.id, delta);
      if (nextId) {
         setSelectedId(nextId);
      }
   };

   /** Selects a rail row and opens its mobile detail step when needed. */
   const selectItem = (id: string): void => {
      if (actionBusy) {
         return;
      }
      setSelectedId(id);
      if (narrow) {
         setShowMobileDetail(true);
      }
   };

   /** Handles previous and next shortcuts only when focus is not editing a field. */
   const handleWorkspaceKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
      if (actionBusy || isEditableTarget(event.target)) {
         return;
      }
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
         event.preventDefault();
         moveSelection(event.key === "ArrowUp" ? -1 : 1);
      }
   };

   if (dataView.loading) {
      return <Stack minHeight={440} alignItems="center" justifyContent="center" spacing={2}>
         <CircularProgress/>
         <Typography color="text.secondary">Loading cohort…</Typography>
      </Stack>;
   }

   if (dataView.error) {
      return <Alert severity="error" action={<Button color="inherit" onClick={() => void dataView.retry()}>
         Retry view
      </Button>}>
         {dataView.error.message || "The cohort could not be loaded"}
      </Alert>;
   }

   const expired = dataView.expiresAt && new Date(dataView.expiresAt).getTime() < Date.now();
   const unavailableMessage = !dataView.dataViewId
      ? "This drilldown is no longer available"
      : expired
         ? "This drilldown has expired"
         : dataView.dataViewType !== DataViewTypeEnum.BrandItem
            ? "This drilldown does not contain stock items"
            : undefined;
   if (unavailableMessage) {
      return <Alert severity="warning" action={<Button color="inherit" onClick={onClose}>Back to dashboard</Button>}>
         {unavailableMessage}. Generate it again from the dashboard.
      </Alert>;
   }

   if (!sourceRows.length) {
      return <Alert severity="info" action={<Button color="inherit" onClick={onClose}>Back to dashboard</Button>}>
         No items in this cohort
      </Alert>;
   }

   return <Box
      data-testid="brand-item-drilldown-workspace"
      tabIndex={-1}
      onKeyDown={handleWorkspaceKeyDown}
      sx={{minHeight: 620, p: {xs: 1, md: 2}}}
   >
      <Stack spacing={2}>
         <Stack direction={{xs: "column", md: "row"}} justifyContent="space-between" alignItems={{md: "center"}} spacing={1}>
            <Button startIcon={<ArrowBack/>} disabled={actionBusy} onClick={onClose}>
               Back to dashboard
            </Button>

            {!!relatedViews.length && <Stack direction="row" spacing={1} alignItems="center">
               <Typography variant="caption" color="text.secondary">Related views</Typography>
               {relatedViews.map(view => view.url
                  ? <Link
                     key={view._id}
                     href={view.url}
                     aria-disabled={actionBusy}
                     onClick={event => {
                        if (actionBusy) {
                           event.preventDefault();
                        }
                     }}
                     sx={actionBusy ? {pointerEvents: "none", color: "text.disabled"} : undefined}
                  >
                     {drilldownTypeLabel(view.dataType)}
                  </Link>
                  : <Typography key={view._id} variant="body2" color="text.disabled">
                     {drilldownTypeLabel(view.dataType)} unavailable
                  </Typography>)}
            </Stack>}
         </Stack>

         {itemState.failedIds.length > 0 && <Alert severity="warning" action={<Button onClick={itemState.retry}>
            Retry items
         </Button>}>
            Some item details could not be loaded.
         </Alert>}

         {search && !visibleRows.length && <Alert severity="info">No cohort items match this search.</Alert>}

         {narrow && showMobileDetail && <Button
            startIcon={<ArrowBack/>}
            disabled={actionBusy}
            onClick={() => setShowMobileDetail(false)}
         >
            Back to cohort
         </Button>}

         <Box
            sx={{
               display: "grid",
               gridTemplateColumns: {xs: "1fr", md: "320px minmax(0, 1fr)", lg: "480px minmax(0, 1fr)"},
               minHeight: 560,
               height: {md: "calc(100vh - 170px)"},
               overflow: "hidden",
               border: 1,
               borderColor: "#e2e2e2",
               borderRadius: 1
            }}
         >
            <Box
               role="region"
               aria-label="Cohort items"
               sx={{
                  display: narrow && showMobileDetail ? "none" : "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  bgcolor: "background.paper",
                  borderRight: {md: 1},
                  borderColor: "#e2e2e2"
               }}
            >
               <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{height: 64, px: 1.5, borderBottom: 1, borderColor: "#e2e2e2", bgcolor: "background.paper"}}
               >
                  {searchOpen
                     ? <TextField
                        autoFocus
                        label="Search"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                        onKeyDown={event => {
                           if (event.key === "Escape") {
                              setSearchOpen(false);
                           }
                        }}
                        size="small"
                        variant="filled"
                        InputLabelProps={{shrink: true}}
                        sx={{flex: 1, border: "1px solid #d0d0d0"}}
                        InputProps={{
                           disableUnderline: true,
                           style: {backgroundColor: "transparent"}
                        }}
                     />
                     : <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography component="h1" variant="h4" noWrap>
                           {dataView.viewName || "Dashboard cohort"}
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                           <Typography variant="body2" color="text.secondary">Stock items</Typography>
                           <Typography variant="body2" fontWeight={600}>
                              {sourceRows.length} {sourceRows.length === 1 ? "item" : "items"}
                           </Typography>
                        </Stack>
                     </Box>}
                  <Tooltip title={searchOpen ? "Hide search" : "Search"}>
                     <span>
                        <IconButton
                           aria-label={searchOpen ? "Hide search" : "Search"}
                           aria-pressed={searchOpen}
                           disabled={actionBusy}
                           size="small"
                           color={searchOpen || !!search ? "primary" : "default"}
                           onClick={() => setSearchOpen(open => !open)}
                        >
                           <Search fontSize="small"/>
                        </IconButton>
                     </span>
                  </Tooltip>
                  <Tooltip title={`Sort cohort (${activeSortLabel})`}>
                     <span>
                        <IconButton
                           aria-label="Sort cohort"
                           aria-controls={sortAnchor ? "cohort-sort-menu" : undefined}
                           aria-expanded={sortAnchor ? "true" : undefined}
                           aria-haspopup="menu"
                           disabled={actionBusy || !sortOptions.length}
                           size="small"
                           onClick={event => setSortAnchor(event.currentTarget)}
                        >
                           <FilterList fontSize="small"/>
                        </IconButton>
                     </span>
                  </Tooltip>
                  <Menu
                     id="cohort-sort-menu"
                     anchorEl={sortAnchor}
                     open={!!sortAnchor}
                     onClose={() => setSortAnchor(null)}
                     MenuListProps={{"aria-label": "Sort cohort options"}}
                     anchorOrigin={{horizontal: "right", vertical: "bottom"}}
                     transformOrigin={{horizontal: "right", vertical: "top"}}
                  >
                     {!activeSort && <MenuItem selected onClick={() => setSortAnchor(null)}>
                        DataView order
                     </MenuItem>}
                     {sortOptions.map(option => <MenuItem
                        key={option.value}
                        selected={option.value === activeSortValue}
                        onClick={() => {
                           setChosenSort(option.sort);
                           setSortAnchor(null);
                        }}
                     >
                        {option.label}
                     </MenuItem>)}
                  </Menu>
               </Stack>
               <Box sx={{flex: 1, minHeight: 0}}>
                  <BrandItemDrilldownList
                     rows={visibleRows}
                     columns={columns}
                     itemsById={itemState.itemsById}
                     selectedId={selectedRow?.id}
                     loading={itemState.loading}
                     disabled={actionBusy}
                     onSelect={selectItem}
                  />
               </Box>
            </Box>
            <Box sx={{
               display: narrow && !showMobileDetail ? "none" : "block",
               overflow: "hidden",
               minHeight: 0
            }}>
               {selectedRow && <BrandItemDrilldownDetail
                  brandHouseId={brandHouseId}
                  row={selectedRow}
                  columns={columns}
                  item={itemState.itemsById.get(selectedRow.id)}
                  position={selectedPosition}
                  total={sortedRows.length}
                  onPrevious={() => moveSelection(-1)}
                  onNext={() => moveSelection(1)}
                  onUpdated={itemState.replaceItem}
                  loading={itemState.loading}
                  busy={actionBusy}
                  onBusyChange={setActionBusy}
               />}
            </Box>
         </Box>
      </Stack>
   </Box>;
};
